import type { Periods, PeriodTime } from '../../features/shared/period-state'
import { PeriodType } from '../../features/shared/period-type'
import type { Grouping, Resolution } from '../../features/revenue/domain/models/range'

export const startOfDay = (now: Date): Date => new Date(now.setHours(0, 0, 0, 0))

const getDateWithDaysOffset = (date: Date, numDays: number): Date => {
  const dayBefore = new Date(date)
  dayBefore.setDate(date.getDate() + numDays)
  return dayBefore
}

export const theDayBefore = (date: Date): Date => getDateWithDaysOffset(date, -1)
export const theDayAfter = (date: Date): Date => getDateWithDaysOffset(date, 1)

export const thirtyDaysBefore = (date: Date): Date => getDateWithDaysOffset(date, -30)

const firstOfTheMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1)
const lastOfTheMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() + 1, 0)
const firstOfPreviousMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() - 1, 1)

const firstOfTheQuarter = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() - (date.getMonth() % 3), 1)
const lastOfTheQuarter = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth() - (date.getMonth() % 3) + 3, 0)
const firstOfPreviousQuarter = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth() - (date.getMonth() % 3) - 3, 1)

const firstOfTheYear = (date: Date): Date => new Date(date.getFullYear(), 0, 1)
const lastOfTheYear = (date: Date): Date => new Date(date.getFullYear(), 11, 31)
const firstOfPreviousYear = (date: Date): Date => new Date(date.getFullYear() - 1, 0, 1)

export const isToday = (date: Date): boolean => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

export const gtDate = (date1: Date, date2: Date): boolean => {
  return date1.toISOString().slice(0, 10) > date2.toISOString().slice(0, 10)
}

export const ltDate = (date1: Date, date2: Date): boolean => {
  return date1.toISOString().slice(0, 10) < date2.toISOString().slice(0, 10)
}

export const eqDate = (date1: Date, date2: Date): boolean => {
  return date1.toISOString().slice(0, 10) === date2.toISOString().slice(0, 10)
}

export const formatDateToTimeAMPM = (date: Date): string => {
  let hours = date.getHours()
  const minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'pm' : 'am'
  hours %= 12
  hours = hours || 12
  const strHours = hours < 10 ? `0${hours}` : hours
  const strMinutes = minutes < 10 ? `0${minutes}` : minutes
  return `${strHours}:${strMinutes} ${ampm}`
}

export const getPeriodsByType = (period: PeriodType, today = new Date()): Periods => {
  const yesterday = theDayBefore(today)
  const lastMonth = firstOfPreviousMonth(today)
  const lastQuarter = firstOfPreviousQuarter(today)
  const lastYear = firstOfPreviousYear(today)
  const sixDaysAgo = getDateWithDaysOffset(today, -6)
  const twentyNineDaysAgo = getDateWithDaysOffset(today, -29)

  switch (period) {
    case PeriodType.Today:
      return {
        current: DatePeriodFactory(today, today),
        prev: DatePeriodFactory(yesterday, yesterday)
      }
    case PeriodType.Yesterday:
      return {
        current: DatePeriodFactory(yesterday, yesterday),
        prev: DatePeriodFactory(theDayBefore(yesterday), theDayBefore(yesterday))
      }
    case PeriodType.Last7Days:
      return {
        current: DatePeriodFactory(sixDaysAgo, today),
        prev: DatePeriodFactory(getDateWithDaysOffset(sixDaysAgo, -7), theDayBefore(sixDaysAgo))
      }
    case PeriodType.Last30Days:
      return {
        current: DatePeriodFactory(twentyNineDaysAgo, today),
        prev: DatePeriodFactory(getDateWithDaysOffset(twentyNineDaysAgo, -30), theDayBefore(twentyNineDaysAgo))
      }
    case PeriodType.CurrentMonth: {
      const firstOfThisMonth = firstOfTheMonth(today)
      const endOfThisMonth = lastOfTheMonth(today)
      const diff = daysDiff(firstOfThisMonth, endOfThisMonth) + 1
      return {
        current: DatePeriodFactory(firstOfThisMonth, endOfThisMonth),
        prev: DatePeriodFactory(getDateWithDaysOffset(firstOfThisMonth, -diff), theDayBefore(firstOfThisMonth))
      }
    }
    case PeriodType.MonthToDate: {
      const firstOfThisMonth = firstOfTheMonth(today)
      const diff = daysDiff(firstOfThisMonth, today) + 1
      return {
        current: DatePeriodFactory(firstOfThisMonth, today),
        prev: DatePeriodFactory(lastMonth, getDateWithDaysOffset(lastMonth, diff - 1))
      }
    }
    case PeriodType.LastMonth: {
      const thisMonth = firstOfTheMonth(today)
      const lastDayOfLastMonth = theDayBefore(thisMonth)
      const diff = daysDiff(lastMonth, lastDayOfLastMonth) + 1
      return {
        current: DatePeriodFactory(lastMonth, lastDayOfLastMonth),
        prev: DatePeriodFactory(getDateWithDaysOffset(lastMonth, -diff), theDayBefore(lastMonth))
      }
    }
    case PeriodType.CurrentQuarter: {
      const firstOfThisQuarter = firstOfTheQuarter(today)
      const endOfThisQuarter = lastOfTheQuarter(today)
      const diff = daysDiff(firstOfThisQuarter, endOfThisQuarter) + 1
      return {
        current: DatePeriodFactory(firstOfThisQuarter, endOfThisQuarter),
        prev: DatePeriodFactory(getDateWithDaysOffset(firstOfThisQuarter, -diff), theDayBefore(firstOfThisQuarter))
      }
    }
    case PeriodType.QuarterToDate: {
      const firstOfThisQuarter = firstOfTheQuarter(today)
      const diff = daysDiff(firstOfThisQuarter, today) + 1
      return {
        current: DatePeriodFactory(firstOfThisQuarter, today),
        prev: DatePeriodFactory(lastQuarter, getDateWithDaysOffset(lastQuarter, diff - 1))
      }
    }
    case PeriodType.LastQuarter: {
      const thisQuarter = firstOfTheQuarter(today)
      const lastDayOfLastQuarter = theDayBefore(thisQuarter)
      const diff = daysDiff(lastQuarter, lastDayOfLastQuarter) + 1
      return {
        current: DatePeriodFactory(lastQuarter, lastDayOfLastQuarter),
        prev: DatePeriodFactory(getDateWithDaysOffset(lastQuarter, -diff), theDayBefore(lastQuarter))
      }
    }
    case PeriodType.CurrentYear: {
      const endOfThisYear = lastOfTheYear(today)
      return {
        current: DatePeriodFactory(firstOfTheYear(today), endOfThisYear),
        prev: DatePeriodFactory(lastYear, theDayBefore(firstOfTheYear(today)))
      }
    }
    case PeriodType.YearToDate: {
      const firstOfThisYear = firstOfTheYear(today)
      const diff = daysDiff(firstOfThisYear, today) + 1
      return {
        current: DatePeriodFactory(firstOfThisYear, today),
        prev: DatePeriodFactory(lastYear, getDateWithDaysOffset(lastYear, diff - 1))
      }
    }
    case PeriodType.LastYear: {
      const thisYear = firstOfTheYear(today)
      const lastDayOfLastYear = theDayBefore(thisYear)
      return {
        current: DatePeriodFactory(lastYear, lastDayOfLastYear),
        prev: DatePeriodFactory(firstOfPreviousYear(lastYear), theDayBefore(lastYear))
      }
    }

    default:
      throw new Error('Invalid period type')
  }
}

export const getPeriodsByDates = (from: Date, to: Date): Periods => {
  const current = DatePeriodFactory(from, to)
  const startPeriod = startOfDay(from)
  const numDays = daysDiff(startPeriod, startOfDay(to)) + 1
  const previousEnd = theDayBefore(startPeriod)
  const previousStart = getDateWithDaysOffset(startPeriod, -numDays)
  const prev = DatePeriodFactory(previousStart, previousEnd)

  return {
    current,
    prev
  }
}

export const DatePeriodFactory = (firstDay: Date, lastDay: Date): PeriodTime => {
  const StartCurrentPeriod = Math.floor(startOfDay(firstDay).getTime() / 1000)
  const EndCurrentPeriod = Math.floor(startOfDay(lastDay).getTime() / 1000)

  return {
    from: StartCurrentPeriod,
    to: EndCurrentPeriod
  }
}

export const formatDateToMonthYear = (date: Date, locale = 'en-US'): string => {
  const d = new Date(date)
  const monthName = d.toLocaleString(locale, { month: 'short' })
  const year = d.getFullYear()

  return `${monthName} ${year}`
}

export const formatDateToUTCMonthYear = (date: Date, locale = 'en-US'): string => {
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const monthName = utcDate.toLocaleString(locale, { month: 'short', timeZone: 'UTC' })
  const year = utcDate.getUTCFullYear()

  return `${monthName} ${year}`
}

export const getDateFormatParts = (): string[] => {
  const formatObj = new Intl.DateTimeFormat(navigator.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date())

  return formatObj.map(({ type }) => type).filter((type) => type !== 'literal')
}

export const parseLocalDateString = (dateString: string): Date => {
  const dateParts = dateString.split(/[^0-9]/).map((part) => Number.parseInt(part, 10))
  const dateObj: Record<string, number> = {}

  getDateFormatParts().forEach((part, index) => {
    if (part === 'year') {
      dateObj.year = dateParts[index]
    } else if (part === 'month') {
      dateObj.month = dateParts[index]
    } else if (part === 'day') {
      dateObj.day = dateParts[index]
    }
  })

  if (dateObj.year !== undefined && dateObj.month !== undefined && dateObj.day !== undefined) {
    return new Date(dateObj.year, dateObj.month - 1, dateObj.day)
  }
  throw new Error('Invalid date format or date string')
}

export const getDateFormatString = (): string => {
  const formatParts = getDateFormatParts()
  const sampleDate = new Date().toLocaleDateString()
  const separators = sampleDate.match(/[^a-zA-Z0-9]/g) ?? ['/']

  const formatMap: Record<string, string> = {
    day: 'dd',
    month: 'mm',
    year: 'yyyy'
  }

  return formatParts.map((part) => formatMap[part]).join(separators[0])
}

export const formatDateToRegular = (date: Date, locale = 'en-US'): string => {
  const d = new Date(date)
  return d.toLocaleString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}

export const daysDiff = (date: Date, date2: Date): number => {
  const DayInSeconds = 24 * 60 * 60 * 1000
  return Math.floor((date2.getTime() - date.getTime()) / DayInSeconds)
}

export const formatMinutesToTimezone = (offset: number): string => {
  const isNegative = offset > 0
  const absoluteOffset = Math.abs(offset)
  const hours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0')
  const minutes = String(absoluteOffset % 60).padStart(2, '0')
  return `${isNegative ? '-' : '+'}${hours}:${minutes}`
}

export const correctDateToCurrentOffset = (timestamp: number): number => {
  const currentOffset = new Date().getTimezoneOffset()
  const inputOffset = new Date(timestamp).getTimezoneOffset()

  return timestamp + (currentOffset - inputOffset) * 60000
}

/**
 * Computes from_date and to_date for revenue API endpoints based on a
 * reference month (e.g. "2026-02-01") and the number of months to include.
 *
 * - toDate  = last day of the reference month
 * - fromDate = first day of (totalMonths - 1) months before the reference month
 */
export const computeDateRange = (month: string, totalMonths: number): { fromDate: string; toDate: string } => {
  const date = new Date(month)
  const year = date.getFullYear()
  const monthIndex = date.getMonth()

  const lastDay = new Date(year, monthIndex + 1, 0)
  const toDate = formatDateToApiString(lastDay)

  const startDate = new Date(year, monthIndex - (totalMonths - 1), 1)
  const fromDate = formatDateToApiString(startDate)

  return { fromDate, toDate }
}

/**
 * Computes from_date, to_date, and resolution for revenue API endpoints
 * based on a SimplePeriodType and Grouping.
 *
 * Resolution mapping is 1:1 with grouping (daily, weekly, monthly).
 * The backend handles weekly aggregation via ClickHouse toStartOfWeek (Sunday start).
 */
export const computeRangeDates = (
  period: SimplePeriodType,
  grouping: Grouping,
  customRange: CustomDateRange | undefined,
  today = new Date()
): { fromDate: string; toDate: string; resolution: Resolution } => {
  const resolution: Resolution = grouping

  if (period === 'custom' && customRange) {
    return {
      fromDate: formatDateToApiString(customRange.from),
      toDate: formatDateToApiString(customRange.to),
      resolution
    }
  }

  // Analytics data refreshes daily at 12:00 UTC with a one-day lag, so "today"
  // never has data. Now that the backend treats to_date as inclusive, every
  // range must end on the last complete day (yesterday). The visible data for
  // each preset is identical to the old exclusive-to_date behavior.
  const anchor = theDayBefore(today)

  switch (period) {
    case 'last_7_days': {
      const fromDate = getDateWithDaysOffset(anchor, -6)
      return { fromDate: formatDateToApiString(fromDate), toDate: formatDateToApiString(anchor), resolution }
    }
    case 'last_30_days': {
      const fromDate = getDateWithDaysOffset(anchor, -29)
      return { fromDate: formatDateToApiString(fromDate), toDate: formatDateToApiString(anchor), resolution }
    }
    case 'last_90_days': {
      const fromDate = getDateWithDaysOffset(anchor, -89)
      return { fromDate: formatDateToApiString(fromDate), toDate: formatDateToApiString(anchor), resolution }
    }
    case 'last_month': {
      const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      return {
        fromDate: formatDateToApiString(firstOfThisMonth),
        toDate: formatDateToApiString(anchor),
        resolution
      }
    }
    case 'last_year': {
      const yearAgo = new Date(anchor.getFullYear() - 1, anchor.getMonth(), anchor.getDate())
      return { fromDate: formatDateToApiString(yearAgo), toDate: formatDateToApiString(anchor), resolution }
    }
    case 'all_time': {
      return { fromDate: '2020-01-01', toDate: formatDateToApiString(anchor), resolution }
    }
    default: {
      const fromDate = getDateWithDaysOffset(anchor, -29)
      return { fromDate: formatDateToApiString(fromDate), toDate: formatDateToApiString(anchor), resolution }
    }
  }
}

/**
 * Formats a Date object to YYYY-MM-DD string for API calls
 */
export const formatDateToApiString = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Converts a timestamp (in seconds) to YYYY-MM-DD string
 */
export const timestampToApiString = (timestamp: number): string => {
  return formatDateToApiString(new Date(timestamp * 1000))
}

export type SimplePeriodType =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'last_month'
  | 'last_year'
  | 'all_time'
  | 'custom'

export interface CustomDateRange {
  from: Date
  to: Date
}

export type CompareMode = 'previous_period' | 'custom_range'

/**
 * Converts simple period type strings to PeriodType enum
 */
export const simplePeriodToPeriodType = (period: SimplePeriodType): PeriodType => {
  switch (period) {
    case 'today':
      return PeriodType.Today
    case 'yesterday':
      return PeriodType.Yesterday
    case 'last_7_days':
      return PeriodType.Last7Days
    case 'last_30_days':
      return PeriodType.Last30Days
    case 'last_90_days':
      return PeriodType.LastQuarter
    case 'last_month':
      return PeriodType.LastMonth
    case 'last_year':
      return PeriodType.LastYear
    case 'all_time':
      return PeriodType.AllTime
    case 'custom':
      return PeriodType.Custom
    default:
      return PeriodType.Last30Days
  }
}

/**
 * Gets date ranges for simple period types with current and previous periods
 */
export const getDateRangesForSimplePeriod = (
  period: SimplePeriodType,
  today = new Date()
): {
  fromDate: string
  toDate: string
  fromPrevious: string
  toPrevious: string
} => {
  // Analytics data refreshes daily at 12:00 UTC with a one-day lag, so "today"
  // never has data. Now that the backend treats to_date as inclusive, every
  // range must be anchored to the last complete day (yesterday). The visible
  // data for each preset is identical to the old exclusive-to_date behavior.
  const anchor = theDayBefore(today)

  let currentStart: Date
  let currentEnd: Date = anchor
  let previousStart: Date
  let previousEnd: Date

  switch (period) {
    // 'today' is not offered by the PeriodSelector UI. If it ever reaches us,
    // treat it as 'yesterday': today has no data yet (one-day data lag).
    case 'today':
    case 'yesterday': {
      currentStart = anchor
      currentEnd = anchor
      previousStart = theDayBefore(anchor)
      previousEnd = theDayBefore(anchor)
      break
    }
    case 'last_7_days': {
      // 7 complete days ending yesterday
      currentStart = getDateWithDaysOffset(anchor, -6)
      previousEnd = theDayBefore(currentStart)
      previousStart = getDateWithDaysOffset(previousEnd, -6)
      break
    }
    case 'last_30_days': {
      currentStart = getDateWithDaysOffset(anchor, -29)
      previousEnd = theDayBefore(currentStart)
      previousStart = getDateWithDaysOffset(previousEnd, -29)
      break
    }
    case 'last_90_days': {
      currentStart = getDateWithDaysOffset(anchor, -89)
      previousEnd = theDayBefore(currentStart)
      previousStart = getDateWithDaysOffset(previousEnd, -89)
      break
    }
    case 'last_month': {
      // Ends on the previous month's last day, which is always before today.
      const lastMonth = firstOfPreviousMonth(today)
      const thisMonth = firstOfTheMonth(today)
      currentStart = lastMonth
      currentEnd = theDayBefore(thisMonth)
      const diff = daysDiff(lastMonth, thisMonth)
      previousStart = getDateWithDaysOffset(lastMonth, -diff)
      previousEnd = theDayBefore(lastMonth)
      break
    }
    case 'last_year': {
      currentStart = new Date(anchor.getFullYear() - 1, anchor.getMonth(), anchor.getDate())
      previousStart = new Date(anchor.getFullYear() - 2, anchor.getMonth(), anchor.getDate())
      previousEnd = theDayBefore(currentStart)
      break
    }
    case 'all_time': {
      currentStart = new Date(2020, 0, 1)
      currentEnd = anchor
      previousStart = currentStart
      previousEnd = currentEnd
      break
    }
    case 'custom': {
      currentStart = getDateWithDaysOffset(anchor, -29)
      previousEnd = theDayBefore(currentStart)
      previousStart = getDateWithDaysOffset(previousEnd, -29)
      break
    }
    default: {
      currentStart = getDateWithDaysOffset(anchor, -29)
      previousEnd = theDayBefore(currentStart)
      previousStart = getDateWithDaysOffset(previousEnd, -29)
    }
  }

  if (!previousEnd) {
    previousEnd = theDayBefore(currentStart)
  }

  return {
    fromDate: formatDateToApiString(currentStart),
    toDate: formatDateToApiString(currentEnd),
    fromPrevious: formatDateToApiString(previousStart),
    toPrevious: formatDateToApiString(previousEnd)
  }
}

/**
 * Gets date ranges for custom date range
 */
/**
 * Returns the appropriate date string to highlight in tables based on grouping.
 * - daily: today's date (YYYY-MM-DD)
 * - weekly: Sunday of the current week (YYYY-MM-DD)
 * - monthly: first of the current month (YYYY-MM-01)
 */
export const getCurrentHighlightDate = (grouping: Grouping, today = new Date()): string => {
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  switch (grouping) {
    case 'daily':
      return `${year}-${month}-${day}`
    case 'weekly': {
      const diff = today.getDay() // Sunday=0 means diff=0
      const sunday = new Date(today)
      sunday.setDate(today.getDate() - diff)
      return formatDateToApiString(sunday)
    }
    case 'monthly':
      return `${year}-${month}-01`
  }
}

export const getDateRangesForCustomPeriod = (
  fromDate: Date,
  toDate: Date
): {
  fromDate: string
  toDate: string
  fromPrevious: string
  toPrevious: string
} => {
  const currentStart = fromDate
  const currentEnd = toDate
  const numDays = daysDiff(currentStart, currentEnd) + 1
  const previousEnd = theDayBefore(currentStart)
  const previousStart = getDateWithDaysOffset(currentStart, -numDays)

  return {
    fromDate: formatDateToApiString(currentStart),
    toDate: formatDateToApiString(currentEnd),
    fromPrevious: formatDateToApiString(previousStart),
    toPrevious: formatDateToApiString(previousEnd)
  }
}
